import { Trans as _Trans } from "../lib/i18n/Trans";
import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import { Button } from "../components/Button";
import { Dialog, DialogContainer, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/Dialog";
import { Input } from "../components/Input";
import { ScrollArea } from "../components/ScrollArea";
import { Separator } from "../components/Separator";
import { Link01, XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
const toolbarRootVariants = cva("inline-flex items-center", {
  variants: {
    variant: {
      overlay: "absolute left-0 top-0 z-10 justify-start gap-3 overflow-hidden rounded-xl bg-primary-50 py-1 pl-3 duration-700 ease-in-out",
      inline: "gap-1 rounded-full bg-linear-to-b from-white to-muted-background px-1.5 py-1 shadow-[0px_1px_4px_0px_rgba(0,0,0,0.12),0px_1px_0px_0px_rgba(0,0,0,0.08)] dark:from-stone-800 dark:to-stone-850 dark:shadow-[inset_0px_1px_0px_0px_rgba(255,255,255,0.06),0px_0px_0px_1px_rgba(0,0,0,0.4),0px_4px_8px_0px_rgba(0,0,0,0.3)]"
    }
  },
  defaultVariants: {
    variant: "inline"
  }
});
const toolbarContentVariants = cva("", {
  variants: {
    variant: {
      overlay: "flex h-full w-max flex-row items-center gap-3 px-3",
      inline: "inline-flex items-center gap-1"
    },
    scrollable: {
      true: "overflow-x-scroll",
      false: ""
    }
  },
  defaultVariants: {
    variant: "inline",
    scrollable: false
  }
});
const toolbarScrollAreaVariants = cva("h-full w-full", {
  variants: {
    variant: {
      overlay: "border-l border-border",
      inline: ""
    }
  },
  defaultVariants: {
    variant: "inline"
  }
});
/**
 * A formatting toolbar for rich-text editing, typically driving a text editor's commands.
 * Use `variant` "inline" to sit within the editor flow or "overlay" for a floating bubble
 * menu, laying out actions with `ToolbarContent` groups, `ToolbarIcon` buttons, and
 * `ToolbarLink`. Use it to present text-formatting controls (bold, italic, lists, code,
 * links); for general page-level actions, use a `Bar` or `HoveringBar` instead.
 *
 * @summary Rich-text formatting toolbar.
 */
function Toolbar({
  variant = "inline",
  children,
  className,
  contentClassName,
  scrollAreaClassName,
  scroll,
  onClose,
  closeButtonProps,
  startSlot
}) {
  const isOverlay = variant === "overlay";
  const isScrollable = scroll !== null && scroll !== void 0 ? scroll : isOverlay;
  const {
    size: closeButtonSizeProp,
    variant: closeVariant = "outline",
    ...restCloseButtonProps
  } = closeButtonProps !== null && closeButtonProps !== void 0 ? closeButtonProps : {};
  const closeButtonSize = closeButtonSizeProp !== null && closeButtonSizeProp !== void 0 ? closeButtonSizeProp : "mini";

  // Wrap cva output in cn() so tailwind-merge collapses conflicting utilities
  // (e.g. a consumer passing `hidden` overrides the base `inline-flex`). Matches
  // Button.tsx's `cn(buttonVariants({ ... }))` pattern. Without it both classes
  // survive and Tailwind's source order decides — `inline-flex` would win.
  const rootClassName = cn(toolbarRootVariants({
    variant,
    className
  }));
  const contentBaseClassName = cn(toolbarContentVariants({
    variant,
    scrollable: isScrollable,
    className: contentClassName
  }));
  const scrollAreaClassNames = cn(toolbarScrollAreaVariants({
    variant,
    className: scrollAreaClassName
  }));
  function renderCloseButton() {
    if (!onClose) {
      return null;
    }
    const buttonProps = {
      variant: closeVariant,
      icon: XClose,
      onClick: onClose,
      ...restCloseButtonProps
    };
    if (closeButtonSize === "mini") {
      return /*#__PURE__*/React.createElement(Button, {
        size: "icon",
        ...buttonProps
      });
    }
    return /*#__PURE__*/React.createElement(Button, {
      size: closeButtonSize,
      ...buttonProps
    });
  }
  const closeButton = renderCloseButton();
  const leadingContent = startSlot !== null && startSlot !== void 0 ? startSlot : closeButton;
  const content = /*#__PURE__*/React.createElement("div", {
    className: contentBaseClassName
  }, children);
  return /*#__PURE__*/React.createElement("div", {
    className: rootClassName
  }, leadingContent, isScrollable ? /*#__PURE__*/React.createElement(ScrollArea, {
    orientation: "horizontal",
    className: scrollAreaClassNames,
    hideScrollBar: true
  }, content) : content);
}
/**
 * Lays out toolbar actions as `groups` separated by vertical dividers, so separators
 * fall in sensible places between related actions.
 *
 * @summary Grouped toolbar actions with separators.
 */
function ToolbarContent({
  groups,
  separatorClassName
}) {
  return /*#__PURE__*/React.createElement(React.Fragment, null, groups.map((group, groupIndex) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: group.id
  }, group.items, groupIndex < groups.length - 1 && /*#__PURE__*/React.createElement(Separator, {
    orientation: "vertical",
    className: cn("my-1", separatorClassName)
  }))));
}
/**
 * An icon button for a toolbar action; set `active` to reflect the formatting applied at
 * the current selection, and provide a `tooltip` to keep the icon identifiable.
 *
 * @summary Toolbar icon button.
 */
function ToolbarIcon({
  icon,
  onClick,
  size,
  active,
  tooltip
}) {
  function handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    onClick();
  }
  if (size === "mini") {
    return /*#__PURE__*/React.createElement(Button, {
      tooltip: tooltip,
      icon: icon,
      onClick: handleClick,
      size: "icon",
      variant: active ? "ghost" : "ghost-secondary"
    });
  }
  return /*#__PURE__*/React.createElement(Button, {
    tooltip: tooltip,
    icon: icon,
    onClick: handleClick,
    size: size,
    variant: active ? "ghost" : "ghost-secondary"
  });
}
/**
 * A link-insertion control for the toolbar: a link icon button paired with a controlled
 * dialog collecting the link text and URL. Own the dialog state via `isOpen` /
 * `onOpenChange` and apply the link in `onSubmit`.
 *
 * @summary Toolbar link-insertion control.
 */
function ToolbarLink({
  isOpen,
  onOpenChange,
  onOpenDialog,
  onSubmit,
  linkText,
  linkUrl,
  onLinkTextChange,
  onLinkUrlChange,
  size,
  active,
  tooltip
}) {
  const {
    _: _t
  } = _useLingui();
  function handleDialogClick(event) {
    event.stopPropagation();
  }
  function handleCancelClick() {
    onOpenChange(false);
  }
  return /*#__PURE__*/React.createElement(Dialog, {
    open: isOpen,
    onOpenChange: onOpenChange
  }, /*#__PURE__*/React.createElement(ToolbarIcon, {
    icon: Link01,
    onClick: onOpenDialog,
    active: active,
    tooltip: tooltip,
    size: size
  }), /*#__PURE__*/React.createElement(DialogContent, {
    onClick: handleDialogClick
  }, /*#__PURE__*/React.createElement(DialogHeader, null, /*#__PURE__*/React.createElement(DialogTitle, null, /*#__PURE__*/React.createElement(_Trans,
  /** i18n */
  {
    id: "p0Iq25",
    message: "Insert link"
  })), /*#__PURE__*/React.createElement(DialogDescription, null, /*#__PURE__*/React.createElement(_Trans,
  /** i18n */
  {
    id: "G-URfF",
    message: "Add a link to your message with custom text."
  }))), /*#__PURE__*/React.createElement(DialogContainer, null, /*#__PURE__*/React.createElement(Input, {
    id: "link-text",
    label: _t(
    /** i18n */
    {
      id: "xeiujy",
      message: "Text"
    }),
    placeholder: _t(
    /** i18n */
    {
      id: "xeiujy",
      message: "Text"
    }),
    value: linkText,
    onChange: event => onLinkTextChange(event.target.value)
  }), /*#__PURE__*/React.createElement(Input, {
    id: "link-url",
    label: _t(
    /** i18n */
    {
      id: "yzF66j",
      message: "Link"
    }),
    placeholder: _t(
    /** i18n */
    {
      id: "yzF66j",
      message: "Link"
    }),
    value: linkUrl,
    autoFocus: true,
    onChange: event => onLinkUrlChange(event.target.value)
  })), /*#__PURE__*/React.createElement(DialogFooter, {
    leftButtonProps: {
      label: _t(
      /** i18n */
      {
        id: "dEgA5A",
        message: "Cancel"
      }),
      variant: "outline",
      onClick: handleCancelClick
    },
    rightButtonProps: {
      label: _t(
      /** i18n */
      {
        id: "tfDRzk",
        message: "Save"
      }),
      variant: "highlight",
      onClick: onSubmit
    }
  })));
}
export { Toolbar, ToolbarContent, ToolbarIcon, ToolbarLink };
//# sourceMappingURL=Toolbar.js.map